import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { AttrPath } from '@cumlaude/metadata';
import { AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { EquivalentAttService } from './equivalent-att.service';
import { QueryParamStateService, sortCodec } from './query-param-state.service';

/**
 * Weet welke query parameters naar een attribuut verwijzen, en zet die om naar de variant die het
 * huidige dashboard aanbiedt. Nodig omdat een dashboard via attOverrides velden kan uitzetten: een url
 * uit een bookmark, van een ander dashboard of van de AI-assistent kan dan een veld noemen dat hier
 * niet hoort.
 *
 * Filters lopen hier niet langs; die worden door de FilterService zelf omgezet, omdat daar de
 * geregistreerde configuraties en de default/non-default-indeling voor nodig zijn.
 */
@Injectable({
	providedIn: 'root',
})
export class QueryParamNormalizerService {
	private readonly equivalentAttService = inject(EquivalentAttService);
	private readonly qp = inject(QueryParamStateService);

	/**
	 * De omgezette parameter, of {@code undefined} als deze parameter niet naar een attribuut verwijst
	 * en dus met rust gelaten moet worden.
	 */
	normaliseer(key: string, value: string, overrides: AttOverrides): Params | undefined {
		switch (key) {
			case 'g':
				return this.qp.encodeGroupQueryParam(
					this.equivalentAttService.naarBeschikbareGroeperingen(this.qp.decodeGroupQueryParam(value), overrides)
				);
			case 'sortOrder':
				return this.normaliseerSortOrder(value, overrides);
			default:
				return undefined;
		}
	}

	/**
	 * Bij een groeperingskolom is de sorteersleutel de att zelf, dus die moet met de groepering mee. Bij
	 * een measure-kolom staat er een kolomlabel; dat matcht nooit een override en blijft dus staan.
	 *
	 * {@code sidebarSortOrder} en {@code vakOrder} gebruiken dezelfde codec maar bevatten geen
	 * attributen, en worden daarom niet omgezet.
	 */
	private normaliseerSortOrder(value: string, overrides: AttOverrides): Params | undefined {
		const sort = sortCodec.decode([value]);
		if (!sort.active) return undefined;

		const [omgezet] = this.equivalentAttService.naarBeschikbareGroeperingen([<AttrPath>sort.active.split('.')], overrides);
		return this.qp.encodeQueryParam('sortOrder', { ...sort, active: omgezet.join('.') });
	}
}
